import type { Video } from "@/types/video";

export interface FilterState {
  subs: string[];
  popularity: string[];
  durations: string[];
  time: string;
  sort: string;
}

export const defaultFilters: FilterState = {
  subs: [],
  popularity: [],
  durations: [],
  time: "all",
  sort: "latest",
};

export const durationOptions = [
  { value: "short", label: "短于 30 分钟" },
  { value: "medium", label: "30 - 60 分钟" },
  { value: "long", label: "长于 60 分钟" },
];

export const popularityOptions = [
  { value: "high", label: "高人气主播" },
  { value: "mid", label: "中人气主播" },
  { value: "low", label: "新秀主播" },
];

export const timeOptions = [
  { value: "all", label: "不限时间" },
  { value: "week", label: "最近一周" },
  { value: "month", label: "最近一月" },
];

export const sortOptions = [
  { value: "latest", label: "最新发布", icon: "ri-time-line" },
  { value: "hot", label: "最热播放", icon: "ri-fire-line" },
  { value: "likes", label: "最多点赞", icon: "ri-thumb-up-line" },
  { value: "duration", label: "时长最长", icon: "ri-timer-line" },
];

const NOW = new Date("2026-10-14").getTime();

function inDurationBucket(seconds: number, bucket: string): boolean {
  if (bucket === "short") return seconds < 1800;
  if (bucket === "medium") return seconds >= 1800 && seconds <= 3600;
  if (bucket === "long") return seconds > 3600;
  return true;
}

function inTimeBucket(dateStr: string, bucket: string): boolean {
  if (bucket === "all") return true;
  const days = (NOW - new Date(dateStr).getTime()) / 86400000;
  if (bucket === "week") return days <= 7;
  if (bucket === "month") return days <= 31;
  return true;
}

export function applyFilters(videos: Video[], filters: FilterState, keyword = ""): Video[] {
  const kw = keyword.trim().toLowerCase();

  const result = videos.filter((v) => {
    if (kw) {
      const haystack = [v.title, v.streamerName, v.subcategory, ...v.tags, v.description]
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(kw)) return false;
    }
    if (filters.subs.length && !filters.subs.includes(v.subcategory)) return false;
    if (filters.popularity.length && !filters.popularity.includes(v.popularity)) return false;
    if (filters.durations.length && !filters.durations.some((b) => inDurationBucket(v.duration, b))) return false;
    if (!inTimeBucket(v.publishedAt, filters.time)) return false;
    return true;
  });

  switch (filters.sort) {
    case "hot":
      result.sort((a, b) => b.views - a.views);
      break;
    case "likes":
      result.sort((a, b) => b.likes - a.likes);
      break;
    case "duration":
      result.sort((a, b) => b.duration - a.duration);
      break;
    default:
      result.sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());
  }

  return result;
}

export function countActiveFilters(filters: FilterState): number {
  return (
    filters.subs.length +
    filters.popularity.length +
    filters.durations.length +
    (filters.time !== "all" ? 1 : 0)
  );
}